import { useContext } from 'react';
import { ConfigContext, http } from '../base-ui/lib';
import { AppUser, AppUserUpdateParams } from '../types/api/AppUser';
import {
	CompanyDiscountsAndProfitUpdateParams,
	CompanyFinancingPlansUpdateParams,
	CompanyLogisticPermitCostsUpdateParams,
	CompanyUpdateParams,
} from '../types/api/Company';
import { Limiter } from '../types/api/Limiter';
import { Proposal, ProposalWhereParams } from '../types/api/Proposal';
import {
	QuoteState,
	Structure,
} from '../types/stateProviders/QuoteStateProvider';
import { Quotation, QuotationCreateParams } from '../types/api/Quotation';
import { SearchParams } from '../types/api/SearchParams';
import {
	AdditionalCost,
	AdditionalCostCreateParams,
	AdditionalCostUpdateParams,
} from '../types/api/AdditionalCost';
import {
	DesignPackage,
	LogisticCosts,
	LogisticCostsPermit,
	LogisticCostsUpdateParams,
} from '../types/api/LogisticCosts';
import { GridViewJobLog, JobLog } from '../types/api/JobLog';
import { ProposalTemplate } from '../types/api/ProposalTemplate';
import { APIError } from '../types/api/Error';
import Accessory from 'src/types/shared/accessory';

export const useApi = () => {
	const { config }: any = useContext(ConfigContext);

	return {
		calculatePricing: {
			post: async (
				quoteId: string,
				options: QuoteState['options'],
				blocks: any
			) => {
				return await http.post(
					{ quoteId, options, blocks },
					`${config.uri.base}/calculatePricing`
				);
			},
		},
		calculatePricingV3: {
			post: async (
				quoteId: string,
				options: QuoteState['options'],
				blocks: any
			) => {
				return await http.post(
					{ quoteId, options, blocks },
					`${config.uri.base}/calculatePricingV3`
				);
			},
		},
		deckingType: {
			getAll: async () => {
				return await http.get({}, `${config.uri.base}/deckingType`);
			},
		},
		railingType: {
			getAll: async () => {
				return await http.get({}, `${config.uri.base}/railingType`);
			},
		},
		limiter: {
			get: async (whereParams: Partial<Limiter>) => {
				return await http.get(whereParams, `${config.uri.base}/limiters`);
			},
		},
		appUser: {
			searchUsers: async () => {
				return await http.get({}, `${config.uri.base}/users`);
			},
			getMe: async () => {
				return (await http.get(
					{},
					`${config.uri.base}/user`
				)) as Promise<AppUser>;
			},
			getAppUserById: async (appUserId: string) => {
				return (await http.get(
					{},
					`${config.uri.base}/user/${appUserId}`
				)) as Promise<AppUser>;
			},
			updateMe: async (
				updateParams: AppUserUpdateParams | CompanyUpdateParams
			) => {
				const formData = new FormData();

				const { profilePicture, ...rest } = updateParams;
				if (profilePicture instanceof File) {
					const allowedMimeTypes = [
						'image/jpeg',
						'image/png',
						'image/svg+xml',
						'image/gif',
						'image/webp',
					];

					if (
						profilePicture.size &&
						!allowedMimeTypes.includes(profilePicture.type)
					) {
						throw new Error('Invalid file type');
					}

					formData.append('profilePicture', profilePicture);
				}

				Object.entries(rest).forEach(([k, v]) =>
					formData.append(k, v as string | Blob)
				);

				return await http.put(formData, `${config.uri.base}/user`, {
					enctype: 'multipart/form-data',
				});
			},
			update: async (
				appUserId: string,
				updateParams: AppUserUpdateParams | CompanyUpdateParams
			) => {
				const formData = new FormData();

				const { profilePicture, ...rest } = updateParams;
				if (profilePicture instanceof File) {
					const allowedMimeTypes = [
						'image/jpeg',
						'image/png',
						'image/svg+xml',
						'image/gif',
						'image/webp',
					];

					if (
						profilePicture.size &&
						!allowedMimeTypes.includes(profilePicture.type)
					) {
						throw new Error('Invalid file type');
					}

					formData.append('profilePicture', profilePicture);
				}

				Object.entries(rest).forEach(([k, v]) =>
					formData.append(k, v as string | Blob)
				);

				return await http.put(
					formData,
					`${config.uri.base}/user/${appUserId}`,
					{ enctype: 'multipart/form-data' }
				);
			},
		},
		quotations: {
			getQuotationById: async (quotationId: string): Promise<Quotation> => {
				return await http.get(
					{},
					`${config.uri.base}/quotation/${quotationId}`
				);
			},
			getQuotations: async () => {
				return await http.get({}, `${config.uri.base}/quotation`);
			},
			// create
			createQuotation: async (quotation: QuotationCreateParams) => {
				return await http.post(quotation, `${config.uri.base}/quotation`);
			},
			// update
			updateQuotation: async (id: string, updateParams: any) => {
				return await http.put(
					updateParams,
					`${config.uri.base}/quotation/${id}`
				);
			},
			// delete
			deleteQuotation: async (quotationId: string) => {
				return await http.delete(
					{},
					`${config.uri.base}/quotation/${quotationId}`
				);
			},
			// duplicate
			duplicateQuotation: async (quotation: Quotation, id: string) => {
				return await http.post(
					quotation,
					`${config.uri.base}/quotation/${id}/duplicate`
				);
			},
			countQuotation: async () => {
				return await http.get({}, `${config.uri.base}/quotation/count`);
			},
			getQuotationFilters: async () => {
				return await http.get({}, `${config.uri.base}/quotation/filters`);
			},
		},
		snapshot: {
			// create
			createSnapshot: async (quoteId: string) => {
				return await http.post({ quoteId }, `${config.uri.base}/snapshot`);
			},
			getLatestSnapshot: async (quoteId: string) => {
				return await http.get(
					{ quoteId },
					`${config.uri.base}/snapshot/latest`
				);
			},
		},
		takeoff: {
			// create
			createTakeoff: async (quoteId: string, pricingOption: any) => {
				return await http.post(
					{ quoteId, pricingOption },
					`${config.uri.base}/takeoff`
				);
			},
			viewTakeoff: async (takeoffId: string) => {
				return await http.get(
					{},
					`${config.uri.base}/takeoff/${takeoffId}/view`
				);
			},
			viewLatestTakeoff: async (quoteId: string) => {
				return await http.get(
					{ quoteId },
					`${config.uri.base}/takeoff/view/latest`
				);
			},
		},
		esign: {
			markSignerAsCompleted: async (processId: string, signerId: string) => {
				return await http.post(
					{ processId, signerId },
					`${config.uri.base}/esign/markSignerAsCompleted`
				);
			},
			getSignInPersonUrl: async (processId: string, signerId: string) => {
				const response = await http.post(
					{ processId, signerId },
					`${config.uri.base}/esign/embedSigning`
				);

				return response;
			},
		},
		jobLog: {
			create: async (quoteId: string) => {
				return await http.post({ quoteId }, `${config.uri.base}/jobLog`);
			},
			search: async (whereParams: {}): Promise<JobLog[]> => {
				return await http.get(whereParams, `${config.uri.base}/jobLog`);
			},
			gridViewSearchAggregate: async (): Promise<GridViewJobLog[]> => {
				return await http.get({}, `${config.uri.base}/jobLog/gridSearch`);
			},
			getById: async (jobLogId: string): Promise<JobLog> => {
				return await http.get({}, `${config.uri.base}/jobLog/${jobLogId}`);
			},
			update: async (jobLogId: string, updateParams: Partial<JobLog>) => {
				return await http.patch(
					updateParams,
					`${config.uri.base}/jobLog/${jobLogId}`
				);
			},
		},
		proposal: {
			search: async (whereParams: ProposalWhereParams): Promise<Proposal[]> => {
				return await http.get(whereParams, `${config.uri.base}/proposal`);
			},
			find: async (whereParams: ProposalWhereParams): Promise<Proposal> => {
				return await http.get(
					whereParams,
					`${config.uri.base}/proposal/single`
				);
			},
			create: async (quoteId: string) => {
				return await http.post({ quoteId }, `${config.uri.base}/proposal`);
			},
		},
		proposalTemplate: {
			get: async () => {
				return await http.get({}, `${config.uri.base}/proposalTemplate`);
			},
			save: async (template: Pick<ProposalTemplate, 'default' | 'custom'>) => {
				return await http.put(template, `${config.uri.base}/proposalTemplate`);
			},
			activate: async (
				templateType: Pick<ProposalTemplate, 'proposalDefault'>
			) => {
				return await http.patch(
					{},
					`${config.uri.base}/proposalTemplate/${templateType}/activate`
				);
			},
			preview: async (
				templateType: Pick<ProposalTemplate, 'proposalDefault'>
			) => {
				return await http.get(
					{},
					`${config.uri.base}/proposalTemplate/${templateType}/preview`
				);
			},
		},
		additionalCost: {
			create: async (
				createParams: AdditionalCostCreateParams
			): Promise<AdditionalCost> => {
				return await http.post(
					createParams,
					`${config.uri.base}/additionalCost`
				);
			},
			search: async (
				whereParams: Partial<AdditionalCost>
			): Promise<AdditionalCost[]> => {
				return await http.get(whereParams, `${config.uri.base}/additionalCost`);
			},
			update: async (id: string, updateParams: AdditionalCostUpdateParams) => {
				return await http.patch(
					updateParams,
					`${config.uri.base}/additionalCost/${id}`
				);
			},
			delete: async (id: string) => {
				return await http.delete({}, `${config.uri.base}/additionalCost/${id}`);
			},
		},
		logisticCosts: {
			search: async (
				whereParams: Partial<LogisticCosts>
			): Promise<LogisticCosts[]> => {
				return await http.get(whereParams, `${config.uri.base}/logisticCosts`);
			},
			find: async (
				whereParams: Partial<LogisticCosts>
			): Promise<LogisticCosts> => {
				return await http.get(
					whereParams,
					`${config.uri.base}/logisticCosts/single`
				);
			},
			update: async (
				id: string,
				updateParams: Partial<LogisticCostsUpdateParams>
			) => {
				return await http.patch(
					updateParams,
					`${config.uri.base}/logisticCosts/${id}`
				);
			},
			getPermits: async (
				whereParams: Partial<LogisticCosts>
			): Promise<LogisticCostsPermit[] | APIError> => {
				const result = await http.get(
					whereParams,
					`${config.uri.base}/logisticCosts/permits`
				);

				if ('errored' in result) {
					return result;
				}
				return result?.rows;
			},
			addPermit: async (
				logisticCostsId: string,
				permit: Partial<LogisticCostsPermit>
			) => {
				return await http.patch(
					{ permit },
					`${config.uri.base}/logisticCosts/${logisticCostsId}/addPermit`
				);
			},
			editPermit: async (
				logisticCostsId: string,
				permit: Partial<LogisticCostsPermit>
			) => {
				return await http.patch(
					{ permit },
					`${config.uri.base}/logisticCosts/${logisticCostsId}/editPermit/${permit._id}`
				);
			},
			deletePermit: async (logisticCostsId: string, permitId: string) => {
				return await http.delete(
					{},
					`${config.uri.base}/logisticCosts/${logisticCostsId}/deletePermit/${permitId}`
				);
			},
			getDesignPackages: async (
				whereParams: Partial<LogisticCosts>
			): Promise<DesignPackage[] | APIError> => {
				const result = await http.get(
					whereParams,
					`${config.uri.base}/logisticCosts/designPackages`
				);
				if ('errored' in result) return result;
				return result?.rows;
			},
			addDesignPackage: async (
				logisticCostsId: string,
				designPackage: Partial<DesignPackage>
			) => {
				return await http.patch(
					{ designPackage },
					`${config.uri.base}/logisticCosts/${logisticCostsId}/addDesignPackage`
				);
			},
			editDesignPackage: async (
				logisticCostsId: string,
				designPackage: Partial<DesignPackage>
			) => {
				return await http.patch(
					{ designPackage },
					`${config.uri.base}/logisticCosts/${logisticCostsId}/editDesignPackage/${designPackage._id}`
				);
			},
			deleteDesignPackage: async (logisticCostsId: string, packageId: string) => {
				return await http.delete(
					{},
					`${config.uri.base}/logisticCosts/${logisticCostsId}/deleteDesignPackage/${packageId}`
				);
			},
		},
		company: {
			search: async (searchQuery: SearchParams) => {
				return await http.get({}, `${config.uri.base}/company/search`, {
					params: { searchQuery },
				});
			},
			get: async () => {
				return await http.get({}, `${config.uri.base}/company`);
			},
			getById: async (companyId: string) => {
				return await http.get({}, `${config.uri.base}/company/${companyId}`);
			},
			getCurrent: async () => {
				return await http.get({}, `${config.uri.base}/company/current`);
			},
			update: async (
				companyId: string,
				updateParams: AppUserUpdateParams | CompanyUpdateParams
			) => {
				const formData = new FormData();

				const { profilePicture, ...rest } = updateParams;
				if (profilePicture instanceof File) {
					const allowedMimeTypes = [
						'image/jpeg',
						'image/png',
						'image/svg+xml',
						'image/gif',
						'image/webp',
					];

					if (
						profilePicture.size &&
						!allowedMimeTypes.includes(profilePicture.type)
					) {
						throw new Error('Invalid file type');
					}

					formData.append('profilePicture', profilePicture);
				}

				Object.entries(rest).forEach(([k, v]) =>
					formData.append(k, v as string | Blob)
				);

				return await http.put(
					formData,
					`${config.uri.base}/company/${companyId}`,
					{ enctype: 'multipart/form-data' }
				);
			},
			updateDetails: async (companyId: string, formData: object) => {
				return await http.put(
					formData,
					`${config.uri.base}/company/${companyId}/details`
				);
			},
			deleteProfilePicture: async (companyId: string) => {
				return await http.delete(
					{},
					`${config.uri.base}/company/${companyId}/profile-picture`
				);
			},
			updateDiscountsAndProfitSettings: async (
				companyId: string,
				params: CompanyDiscountsAndProfitUpdateParams
			) => {
				return await http.put(
					params,
					`${config.uri.base}/company/${companyId}/settings/discounts-and-profit`
				);
			},
			updateLogisticPermitCosts: async (
				companyId: string,
				params: CompanyLogisticPermitCostsUpdateParams
			) => {
				return await http.put(
					params,
					`${config.uri.base}/company/${companyId}/settings/logistic-costs`
				);
			},
			updateFinancingPlans: async (
				companyId: string,
				params: CompanyFinancingPlansUpdateParams
			) => {
				return await http.put(
					params,
					`${config.uri.base}/company/${companyId}/settings/financing-plans`
				);
			},
			deleteCompany: async (tenantId: string, companyId: string) => {
				await http.delete({}, `${config.uri.base}/company/${companyId}`);

				await http.delete({}, `${config.uri.base}/tenant/${tenantId}`);
			},
		},
		cognito: {
			loginWithCode: async (code: string, auth: any) => {
				const result = await http.get(
					{},
					`${config.uri.base}/login?code=${code}`
				);

				auth.setBearerToken(result);
			},
			logout: async () => {
				await http.put({}, `${config.uri.base}/logout`);
			},
		},
		accessory: {
			getAccessories: async () => {
				return await http.get({}, `${config.uri.base}/accessory`);
			},
			createAccessory: async (accessory: Accessory) => {
				return await http.post(accessory, `${config.uri.base}/accessory`);
			},
			updateAccessory: async (accessory: Accessory) => {
				return await http.put(accessory, `${config.uri.base}/accessory`);
			},
			deleteAccessory: async (accessoryId: string) => {
				return await http.delete(
					{},
					`${config.uri.base}/accessory/${accessoryId}`
				);
			},
			getAccessoryCalculationTypes: async () => {
				return await http.get(
					{},
					`${config.uri.base}/accessory/calculationTypes`
				);
			},
		},
		handleEvent: {
			quoteSaved: async (quoteId: string) => {
				return await http.post(
					{},
					`${config.uri.base}/handleEvent/quoteSaved/${quoteId}`
				);
			},
			quotePriced: async (quoteId: string) => {
				return await http.post(
					{},
					`${config.uri.base}/handleEvent/quotePriced/${quoteId}`
				);
			},
			proposalModeEnabled: async (quoteId: string) => {
				return await http.post(
					{},
					`${config.uri.base}/handleEvent/proposalModeEnabled/${quoteId}`
				);
			},
		},
		utils: {
			getMaterialSpans: async (structure: Structure) => {
				return await http.get(
					structure,
					`${config.uri.base}/utils/getMaterialSpans`
				);
			},
		},
	};
};
